import * as React from "react";
import i18n from "i18n";
import { observer } from "mobx-react";
import NumberFormat from "react-number-format";
import { WBSStore } from "modules/WBS/WBSStore";
import { WBSPropertiesStore } from "modules/WBS/WBSPropertiesStore";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  withStyles,
  Tooltip,
} from "@material-ui/core";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { List } from "react-virtualized";
import { EContentType } from "common/enums";
import { roundBy, times } from "utils/DecimalUtils";
import { formatNumber } from "utils/MathUtils";

const ABC_COLOR = [
  { pMin: 0, pMax: 80, c: "255,69,0", toolbar: "Curva A - De 0% a 80%" },
  { pMin: 80, pMax: 95, c: "255,140,0", toolbar: "Curva B - De 80,01% a 95%" },
  {
    pMin: 95,
    pMax: 101,
    c: "193,210,71",
    toolbar: "Curva C - De 95,01% a 100%",
  },
];

const COLUMN_SIZE = [
  100, // rowCode
  220, // description
  94, // quantity
  90, // unit
  110, // unitCost
  110, // total_cost
  100, // contentType
  90, // origin
  90, // percentage
  90, // cumulative_percentage
];

const columnWidthConst = ((window.innerWidth - 290) * 100) / 1094 / 100;
const styles = (theme) => ({
  overflow: {
    overflow: "auto",
    height: "calc(100vh - 241px)",
  },
  table: {
    fontFamily: "Roboto",
  },
  tableHead: {
    fontSize: "12px",
    color: "#5c5c5c",
    "& th": {
      padding: "15px",
      border: "0px",
    },
  },
  relative: {
    position: "relative" as any,
  },
  rowRoot: {
    height: "50px",
    "& > :last-child": {
      padding: "0px",
      borderRight: "0",
    },
  },
  cellWrapper: {
    cursor: "pointer",
    transition: "all 0.3s ease-in",
    "&:hover": {
      backgroundColor: "#EDF2CB",
      "& > img": {
        opacity: 1,
      },
    },
    position: "sticky" as any,
    top: "0px",
    background: "white",
    zIndex: 10 as any,
  },
  input: {
    outlineStyle: "none" as any,
    paddingRight: "10px",
    paddingLeft: "10px",
    color: "#666",
    height: "24px",
    width: "100%",
    border: "0",
    backgroundColor: "unset",
    fontSize: "13px",
  },
  noPadding: {
    padding: "0px !important",
    width: 200,
    borderRight: "1px solid #e0e0e0",
  },

  hover: {
    "&:hover": {
      backgroundColor: "#FAFAFA",
    },
  },
});

type IProps = {
  classes: any;
  treeReference: Boolean;
};

@observer
class WBSResources extends React.Component<IProps> {
  state = {
    currentClmn: "",
    inputValue: "" as string | number,
    numericInputValue: -1,
    // allResources: [] as any,
    selectedLine: -1,
    resourcesDataTable: [] as any,
    loading: undefined,
    needUpdate: false,
    wbsId: "",
  };

  constructor(props) {
    super(props);
  }

  async componentWillReceiveProps() {
    const changeWBS =
      WBSStore.WBSRef && this.state.wbsId !== WBSStore.WBSRef.id;
    if (changeWBS || WBSStore.updateComposition) {
      this.refreshResourcesData();
    }

    // Só atualiza quando estiver na aba de insumos
    if (WBSStore.tab === 1 && !this.state.loading) {
      // Carrega todos os dados das tasks e resources porque com o código inicial os dados de dentro das composições não haviam
      if (
        WBSStore.resourcesDataTable.length === 0 &&
        this.state.loading === undefined
      ) {
        await this.setState({ loading: true }, () => {
          WBSStore.loading = true;
        });
        let resourcesData = await WBSStore.loadResourcesData();
        this.setState({
          resourcesDataTable: resourcesData,
          wbsId: WBSStore.WBSRef.id,
        });
        await this.setState({ loading: false }, () => {
          WBSStore.loading = false;
        });
      } else if (WBSStore.updateResource) {
        await this.setState({ loading: true }, () => {
          WBSStore.loading = true;
        });
        WBSStore.orderByDirection =
          WBSStore.orderByDirection == "asc" ? "desc" : "asc";
        let resourcesData = await WBSStore.loadResourcesData();
        this.setState({ resourcesDataTable: resourcesData });
        await this.setState({ loading: false }, () => {
          WBSStore.loading = false;
          WBSStore.updateResource = false;
        });
      }
    }
  }

  refreshResourcesData() {
    this.setState({
      loading: undefined,
      resourcesDataTable: [],
      currentClmn: "",
      inputValue: "" as string | number,
      numericInputValue: -1,
      selectedLine: -1,
      needUpdate: false,
    });
    WBSStore.clearResourcesDataTable();
  }

  verifySelectedLine(index) {
    if (index != this.state.selectedLine) {
      this.setState({ selectedLine: index });
      WBSStore.selectedLineIndexResources = index;
    }
  }

  handleClickTitle(columnName: string) {
    const index = this.state.selectedLine;
    WBSStore.selectedLineIndexResources = -1;

    const orderedResources = WBSStore.orderResourcesBy(
      this.state.resourcesDataTable,
      columnName
    );
    this.setState({ resourcesDataTable: orderedResources });
    WBSStore.selectedLineIndexResources = index;
  }

  tooltipColor(abc) {
    return (
      <>
        <div
          style={{
            height: 11,
            width: 11,
            backgroundColor: "rgb(" + abc.c + ")",
            display: "inline-block",
            marginRight: "4px",
          }}
        ></div>
        {abc.toolbar}
        <br />
      </>
    );
  }

  headerRenderer = ({
    key, // Unique key within array of rows
    style, // Style object to be applied to row (to position it)
  }) => {
    const { classes } = this.props,
      rowClasses = { root: classes.rowRoot };

    const tableCell = (index_name, name, width) => {
      let tAlign = index_name === "description" ? "left" : "center";
      let padding = index_name === "description" ? 15 : 0;
      return (
        <Tooltip
          title={
            index_name === "percentage" ? (
              i18n.resources.percentage
            ) : index_name === "cumulative_percentage" ? (
              <div style={{ fontSize: 11 }}>
                {i18n.resources.cumulativePercentage + ":"}
                <br />
                {ABC_COLOR.map((abc) => this.tooltipColor(abc))}
              </div>
            ) : (
              name
            )
          }
        >
          <TableCell
            className={classes.cellWrapper}
            key={`header_${name}`}
            style={{
              textAlign: tAlign as any,
              width,
              padding,
            }}
            onClick={() => this.handleClickTitle(index_name)}
          >
            <div style={{ textAlign: tAlign as any }}>
              {name}
              {WBSStore.orderBy === index_name &&
                (WBSStore.orderByDirection === "asc" ? (
                  <KeyboardArrowUp
                    style={{
                      height: 15,
                      width: 15,
                      marginLeft: 2,
                      verticalAlign: "top",
                    }}
                  ></KeyboardArrowUp>
                ) : (
                  <KeyboardArrowDown
                    style={{
                      height: 15,
                      width: 15,
                      marginLeft: 2,
                      verticalAlign: "top",
                    }}
                  ></KeyboardArrowDown>
                ))}
            </div>
          </TableCell>
        </Tooltip>
      );
    };
    return (
      <Table key={key} style={style} className={classes.table}>
        <TableHead className={classes.tableHead}>
          <TableRow classes={rowClasses}>
            {tableCell(
              "rowCode",
              i18n.project.rightSideMenu.code,
              COLUMN_SIZE[0]
            )}
            {tableCell(
              "description",
              i18n.resources.description,
              COLUMN_SIZE[1]
            )}
            {tableCell("quantity", i18n.resources.quantity, COLUMN_SIZE[2])}
            {tableCell("unit", i18n.resources.unity, COLUMN_SIZE[3])}
            {tableCell("unitCost", i18n.resources.unitCost, COLUMN_SIZE[4])}
            {tableCell("total_cost", i18n.resources.total, COLUMN_SIZE[5])}
            {tableCell(
              "contentType",
              i18n.resources.classification,
              COLUMN_SIZE[6]
            )}
            {tableCell("origin", i18n.resources.origin, COLUMN_SIZE[7])}
            {tableCell("percentage", "%", COLUMN_SIZE[8])}
            {tableCell("cumulative_percentage", "% Acum.", COLUMN_SIZE[9])}
          </TableRow>
        </TableHead>
      </Table>
    );
  };

  // Salva no state a cada alteração, para posteriormente salvar na store
  handleChangeInput(name_value, value, index) {
    let resourcesDataTable: any = this.state.resourcesDataTable;
    if (
      (name_value === "total_cost" || name_value === "unitCost") &&
      value == undefined
    ) {
      value = 0;
    }
    resourcesDataTable[index][name_value] = value;

    this.setState({
      resourcesDataTable,
      needUpdate: true,
    });
  }

  handleKeyPress = (index, name_value, e) => {
    e = e || window.event;
    let key = "which" in e ? e.which : e.keyrowCode,
      activeElement = document.activeElement as any;
    if (key === 13) {
      this.killEvent(e);
      if (activeElement) activeElement.blur();
      let dataKeyElement = document.getElementById(
        `ins.${index + 1}.${name_value}`
      );
      dataKeyElement && dataKeyElement.focus();
    }
  };
  handleKeyDown = (index, name_value) => (e) => {
    e = e || window.event;
    let key = "which" in e ? e.which : e.keyrowCode;
    if (key === 38) {
      this.killEvent(e);
      let dataKeyElement = document.getElementById(
        `ins.${index - 1}.${name_value}`
      );
      dataKeyElement && dataKeyElement.focus();
    } else if (key === 40) {
      this.killEvent(e);
      let dataKeyElement = document.getElementById(
        `ins.${index + 1}.${name_value}`
      );
      dataKeyElement && dataKeyElement.focus();
    }
  };

  killEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Atualiza a store apenas quando tira o foco do campo
  async handleBlur(name_value, index) {
    if (this.state.needUpdate == false) return;
    let resourcesDataTable: any = this.state.resourcesDataTable;
    if (name_value === "unitCost") {
      let newValue =
        resourcesDataTable[index].quantity * resourcesDataTable[index].unitCost;
      // // Só calcula se mudar o valor
      if (resourcesDataTable[index].total_cost !== newValue) {
        // Atualiza a soma do total com o novo custo unitário
        WBSStore.resourcesDataTable[index].total_cost = newValue;
        WBSStore.resourcesDataTable[index].unitCost =
          resourcesDataTable[index].unitCost;
        // Se for custo unitário vai recalcular recursivamente todos os calculos que usam esse recurso
        await WBSStore.recursiveUpdateWBSSubcollection(
          resourcesDataTable[index].contentId,
          resourcesDataTable[index].unitCost
        );
        let resourcesPercent = WBSStore.calculatePercentage();
        this.setState({ resourcesDataTable: resourcesPercent });
      }
    } else {
      // Salva no banco o recurso atualizado
      WBSStore.updateWBSSubcollection(
        resourcesDataTable[index].id,
        resourcesDataTable[index].contentId,
        { [name_value]: resourcesDataTable[index][name_value] },
        "wbsResources"
      );
    }
  }

  columnWidth(width) {
    return width * columnWidthConst;
  }

  cellRenderer = ({
    key, // Unique key within array of rows
    style, // Style object to be applied to row (to position it)
    index, // Index of row
  }) => {
    const { classes } = this.props;
    const { resourcesDataTable } = this.state;

    const tableCell = (
      name_value,
      width,
      value,
      disabled,
      numeric,
      textAlign,
      percentage = false
    ) => {
      let color = "0, 255, 0";
      if (name_value == "cumulative_percentage" && value != undefined) {
        let tmpColor = ABC_COLOR.filter(
          (abc) => value > abc.pMin && value <= abc.pMax
        )[0];
        if (tmpColor != undefined) {
          color = tmpColor.c;
        }
      }
      /*
        PRUBK-824: Para mante em branco o código dos insumos que não possuem código, quando houver reordenação ná tabela de insumos.
      */
      if (name_value == "rowCode" && value == undefined) {
        value = "";
      }

      return (
        <TableCell
          className={classes.noPadding}
          style={{
            width,
            borderTop: "1px solid #e0e0e0",
            backgroundColor:
              this.state.selectedLine === index &&
              name_value != "cumulative_percentage"
                ? "rgba(171, 194, 0, 0.2)"
                : disabled
                ? "rgba(0,0,0,0.02)"
                : "",
            textAlign,
          }}
        >
          {percentage ? (
            <div
              style={{
                position: "relative",
                height: "24px",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  width: `${value && isFinite(value) ? value : 0}%`,
                  backgroundImage:
                    name_value == "cumulative_percentage"
                      ? "linear-gradient(to right, rgba(" +
                        color +
                        ", 1), rgba(" +
                        color +
                        ", 0.1))"
                      : "linear-gradient(to right, rgba(171,194,0, 1), rgba(171,194,0, 0.1))",
                  height: "24px",
                  top: 1,
                  zIndex: 1,
                }}
              ></div>
              <input
                id={`ins.${index}.${name_value}`}
                value={value && isFinite(value) ? value.toFixed(2) + "%" : "0%"}
                className={classes.input}
                onFocus={() => this.verifySelectedLine(index)}
                onKeyDown={this.handleKeyDown(index, name_value)}
                onKeyPress={(e) => this.handleKeyPress(index, name_value, e)}
                style={{
                  textAlign,
                  caretColor: "transparent",
                  position: "relative",
                  zIndex: 10,
                }}
              />
            </div>
          ) : numeric ? (
            <NumberFormat
              id={`ins.${index}.${name_value}`}
              autoComplete={"off"}
              value={roundBy(
                value,
                WBSPropertiesStore.getRules(
                  name_value === "unitCost" ? undefined : 2
                )
              )}
              className={classes.input}
              {...(i18n.language === "PT"
                ? { decimalSeparator: ",", thousandSeparator: "." }
                : { decimalSeparator: ".", thousandSeparator: "," })}
              onValueChange={(e) =>
                this.handleChangeInput(name_value, e.floatValue, index)
              }
              onBlur={() => this.handleBlur(name_value, index)}
              onFocus={() => this.verifySelectedLine(index)}
              onKeyDown={this.handleKeyDown(index, name_value)}
              onKeyPress={(e) => this.handleKeyPress(index, name_value, e)}
              fixedDecimalScale={true}
              decimalScale={
                name_value === "unitCost" ? WBSStore.WBS.data.decimalPlaces : 2
              }
              readOnly={disabled}
              style={
                disabled
                  ? {
                      textAlign,
                      caretColor: "transparent",
                    }
                  : {
                      textAlign,
                    }
              }
            />
          ) : disabled ? (
            <input
              id={`ins.${index}.${name_value}`}
              value={
                name_value === "quantity" && i18n.language === "PT"
                  ? formatNumber(value, 0, 8)
                  : value
              }
              className={classes.input}
              onFocus={() => this.verifySelectedLine(index)}
              onKeyDown={this.handleKeyDown(index, name_value)}
              onKeyPress={(e) => this.handleKeyPress(index, name_value, e)}
              style={{
                textAlign,
                caretColor: "transparent",
              }}
            />
          ) : (
            <input
              id={`ins.${index}.${name_value}`}
              type={"text"}
              autoComplete={"off"}
              value={value}
              className={classes.input}
              onChange={(e) =>
                this.handleChangeInput(name_value, e.target.value, index)
              }
              onBlur={() => this.handleBlur(name_value, index)}
              onFocus={() => this.verifySelectedLine(index)}
              onKeyDown={this.handleKeyDown(index, name_value)}
              onKeyPress={(e) => this.handleKeyPress(index, name_value, e)}
              style={{
                textAlign,
              }}
            />
          )}
        </TableCell>
      );
    };
    const item = resourcesDataTable[index];

    var contentType = "";
    // “labor” - Quando for mão de obra
    // “equipment” - Quando for equipamento
    // “material” - Quando for material
    // “other” - Quando for outro.
    if (
      item.contentType === EContentType.LABOR ||
      item.contentType === EContentType.EQUIPMENT ||
      item.contentType === EContentType.MATERIAL
    ) {
      contentType = i18n.project.rightSideMenu.categories[item.contentType];
    } else {
      contentType = i18n.project.rightSideMenu.categories.other;
    }

    return (
      <div
        key={key}
        style={style}
        className={classes.hover}
        onClick={() => this.verifySelectedLine(index)}
      >
        {tableCell(
          "rowCode",
          this.columnWidth(COLUMN_SIZE[0]),
          item.rowCode,
          false,
          false,
          "left"
        )}
        {tableCell(
          "description",
          this.columnWidth(COLUMN_SIZE[1]),
          item.description,
          false,
          false,
          "left"
        )}
        {tableCell(
          "quantity",
          this.columnWidth(COLUMN_SIZE[2]),
          item.quantity,
          true,
          false,
          "center"
        )}
        {tableCell(
          "unit",
          this.columnWidth(COLUMN_SIZE[3]),
          item.unit,
          false,
          false,
          "center"
        )}
        {tableCell(
          "unitCost",
          this.columnWidth(COLUMN_SIZE[4]),
          item.unitCost,
          false,
          true,
          "right"
        )}
        {tableCell(
          "total_cost",
          this.columnWidth(COLUMN_SIZE[5]),
          item.total_cost,
          true,
          true,
          "right"
        )}
        {tableCell(
          "contentType",
          this.columnWidth(COLUMN_SIZE[6]),
          contentType,
          true,
          false,
          "left"
        )}
        {tableCell(
          "origin",
          this.columnWidth(COLUMN_SIZE[7]),
          item.origin,
          true,
          false,
          "left"
        )}
        {tableCell(
          "percentage",
          this.columnWidth(COLUMN_SIZE[8]),
          item.percentage,
          true,
          false,
          "right",
          true
        )}
        {tableCell(
          "cumulative_percentage",
          this.columnWidth(COLUMN_SIZE[9]),
          item.cumulative_percentage,
          true,
          false,
          "right",
          true
        )}
      </div>
    );
  };
  render() {
    return (
      <>
        <List
          width={window.innerWidth - 290}
          height={50}
          rowCount={1}
          rowHeight={50}
          rowRenderer={this.headerRenderer}
          style={{ overflowY: "unset" }}
          overscanRowCount={0}
        />
        <List
          width={window.innerWidth - 290}
          height={window.innerHeight - 243}
          rowCount={this.state.resourcesDataTable.length}
          rowHeight={25}
          rowRenderer={this.cellRenderer}
          style={{ overflowY: "unset", fontSize: "13px", color: "#666" }}
          overscanRowCount={10}
        />
      </>
    );
  }
}
export default withStyles(styles)(WBSResources);



// WEBPACK FOOTER //
// ./src/modules/WBS/components/layouts/WBSResources.tsx